import {
  generateAverage,
  generateRatio,
  getAverage,
} from '../../../Component/Analytics/utils';

// Stat tile rates for one message, from the counts returned by
// GET /analytics-v2/messages/:orgId. The formulas copy the current Analytics
// page (Component/Analytics/.../AllChannels.js, Email.js and the A/B versions):
// - smart-resend unique opens and clicks are added to the original's counts
// - an A/B message shows the mean of the A and B rates, each rounded to one
//   decimal first, as generateAverage does

export const TILES = [
  { key: 'delivery', label: 'Delivery' },
  { key: 'open', label: 'Open-rate' },
  { key: 'clickThrough', label: 'Click-through' },
  { key: 'bounce', label: 'Bounce Rate' },
  { key: 'drop', label: 'Drop rate' },
  { key: 'spam', label: 'Spam Report' },
];

// Same as generateRatio in Component/Analytics/utils.js: 0 when either
// number is 0 or not a number.
const ratio = (numerator, denominator) => {
  if (!numerator || !denominator) return 0;
  return (numerator / denominator) * 100;
};

const roundToOneDecimal = value => Number(value.toFixed(1));

// Rates for one version of a message. Delivery and Bounce add email and text
// counts together; the other tiles use email counts.
const getVersionRates = (email, text, resendEmail) => {
  const uniqueOpen = email.uniqueOpen + (resendEmail?.uniqueOpen || 0);
  const uniqueClick = email.uniqueClick + (resendEmail?.uniqueClick || 0);
  return {
    delivery: ratio(email.delivered + text.delivered, email.sent + text.sent),
    open: ratio(uniqueOpen, email.delivered),
    clickThrough: ratio(uniqueClick, uniqueOpen),
    bounce: ratio(email.bounce + text.bounced, email.sent + text.sent),
    drop: ratio(email.dropped, email.sent),
    spam: ratio(email.spamReport, email.sent),
  };
};

const hasSentAnything = ({ email, text }) => email.sent + text.sent > 0;

// Returns { delivery, open, clickThrough, bounce, drop, spam } as numbers, or
// null for every tile when a scheduled or cancelled message has no events.
export const getTileRates = message => {
  const { stats, status } = message;
  const versions = stats.versionB ? [stats, stats.versionB] : [stats];

  if (
    (status === 'scheduled' || status === 'cancelled') &&
    !versions.some(hasSentAnything)
  ) {
    return Object.fromEntries(TILES.map(({ key }) => [key, null]));
  }

  const ratesA = getVersionRates(
    stats.email,
    stats.text,
    stats.smartResend?.email
  );
  if (!stats.versionB) return ratesA;

  const ratesB = getVersionRates(
    stats.versionB.email,
    stats.versionB.text,
    stats.smartResend?.versionB?.email
  );
  return Object.fromEntries(
    TILES.map(({ key }) => [
      key,
      (roundToOneDecimal(ratesA[key]) + roundToOneDecimal(ratesB[key])) / 2,
    ])
  );
};

export const formatRate = rate => (rate === null ? '–' : `${rate.toFixed(1)}%`);

// Emails delivered for the message, A and B together.
export const getEmailDelivered = ({ stats }) =>
  stats.email.delivered + (stats.versionB?.email.delivered || 0);

// ---------------------------------------------------------------------------
// Detail page tiles. The values copy the current Analytics page cards
// (MessageAnalytics/Components/AllChannels.js, Email.js, Text.js, Card.js and
// the MessageAnalyticsABversion versions), including their text formatting,
// so a tile reads the same on both pages. Under All channels the detail page
// shows six tiles (the current page shows three): Delivery and Bounce add
// email and text counts, the others use email counts.

export const CHANNELS = [
  { key: 'all', label: 'All channels' },
  { key: 'email', label: 'Email' },
  { key: 'text', label: 'Text' },
];

export const CHANNEL_TILES = {
  all: ['delivery', 'open', 'clickThrough', 'bounce', 'drop', 'spam'],
  email: ['delivery', 'open', 'clickThrough', 'bounce', 'drop', 'spam'],
  text: ['delivery', 'bounce'],
};

export const TILE_NAMES = {
  delivery: 'Delivery Rate',
  open: 'Open Rate',
  clickThrough: 'Click-Through Rate',
  bounce: 'Bounce Rate',
  drop: 'Drop Rate',
  spam: 'Spam Report Rate',
};

// Hover text per channel and tile, from the current page's cards.
const OPEN_TOOLTIP =
  "The number of contacts who opened your email at least once. (It's a great indicator of subject line quality.)";
const CLICK_TOOLTIP =
  "The number of contacts who clicked a link in your email after opening it. (It's a great indicator of content quality.)";
const EMAIL_BOUNCE_TOOLTIP =
  'Usually a result of sending to a bad email address.';
const DROP_TOOLTIP =
  '"Dropped" means that an email was not sent because of an unresolved "Bounce" or "Spam Report" issue for that contact.';
const SPAM_TOOLTIP =
  'The number of contacts who reported your message as spam.';
export const TILE_TOOLTIPS = {
  all: {
    delivery: 'The average of your email and text successful deliveries.',
    open: 'The average of your email unique open rates.',
    clickThrough: 'The average of your email and text click-to-open rates.',
    bounce: EMAIL_BOUNCE_TOOLTIP,
    drop: DROP_TOOLTIP,
    spam: SPAM_TOOLTIP,
  },
  email: {
    delivery:
      'The number of unique contacts that successfully received their email. If multiple contacts have the same email address, only one of them will be emailed.',
    open: OPEN_TOOLTIP,
    clickThrough: CLICK_TOOLTIP,
    bounce: EMAIL_BOUNCE_TOOLTIP,
    drop: DROP_TOOLTIP,
    spam: SPAM_TOOLTIP,
  },
  text: {
    delivery:
      'The % of members successfully delivered to (Delivered / Recipients)',
    bounce: 'Usually a result of sending to a bad phone number.',
  },
};

// Open and Click-through show the resend as a change ("+4.0% from resend"),
// the others as the resend's own rate ("Resend: 98.0%").
const COMPARATIVE_TILES = ['open', 'clickThrough'];

// One version's numbers for a tile:
// - value: the rate as the current page writes it ("40.0%", or "0%")
// - count: the count without the resend
// - resendValue: the resend's rate or change, or null without a resend
// - resendCount: the resend's count
const getVersionTile = (tileKey, channel, email, text, resend) => {
  const withResend = (base, resendCount, value, resendValue) => ({
    value,
    count: base,
    resendValue: resend ? resendValue : null,
    resendCount: resend ? resendCount : 0,
  });

  if (channel === 'text') {
    const count = tileKey === 'delivery' ? text.delivered : text.bounced;
    return {
      value: generateRatio(count, text.sent),
      count,
      resendValue: null,
      resendCount: 0,
    };
  }

  const textDelivered = channel === 'all' ? text.delivered : 0;
  const textBounced = channel === 'all' ? text.bounced : 0;
  const textSent = channel === 'all' ? text.sent : 0;
  const resendOpen = resend?.uniqueOpen || 0;
  const resendClick = resend?.uniqueClick || 0;

  switch (tileKey) {
    case 'delivery':
      return withResend(
        email.delivered + textDelivered,
        resend?.delivered,
        generateRatio(email.delivered + textDelivered, email.sent + textSent),
        resend && generateRatio(resend.delivered, resend.sent)
      );
    case 'open':
      return withResend(
        email.uniqueOpen,
        resendOpen,
        generateRatio(email.uniqueOpen + resendOpen, email.delivered),
        resend && generateRatio(resend.uniqueOpen, email.delivered)
      );
    case 'clickThrough':
      return withResend(
        email.uniqueClick,
        resendClick,
        generateRatio(
          email.uniqueClick + resendClick,
          email.uniqueOpen + resendOpen
        ),
        // The change in whole percent, as calcSmartResendClickThroughRate.
        resend &&
          parseInt(
            generateRatio(
              email.uniqueClick + resendClick,
              email.uniqueOpen + resendOpen
            ),
            10
          ) -
            parseInt(generateRatio(email.uniqueClick, email.uniqueOpen), 10) +
            '%'
      );
    case 'bounce':
      return withResend(
        email.bounce + textBounced,
        resend?.bounce,
        generateRatio(email.bounce + textBounced, email.sent + textSent),
        resend && generateRatio(resend.bounce, resend.sent)
      );
    case 'drop':
      return withResend(
        email.dropped,
        resend?.dropped,
        generateRatio(email.dropped, email.sent),
        resend && generateRatio(resend.dropped, resend.sent)
      );
    default:
      return withResend(
        email.spamReport,
        resend?.spamReport,
        generateRatio(email.spamReport, email.sent),
        resend && generateRatio(resend.spamReport, resend.sent)
      );
  }
};

// "No data" / "No data yet" in place of the tile values, as Card.js and
// CardABversion.js show them, or null. Sending messages count as sent.
const getNoDataText = (message, isAB) => {
  if (message.status === 'scheduled') return 'No data yet';
  if (message.status === 'cancelled' && (isAB || !message.smartResend)) {
    return 'No data';
  }
  return null;
};

// The detail page tiles for a message (a row from the list endpoint) and
// channel ('all', 'email' or 'text').
// Single version: { key, name, tooltip, noDataText, value, count, resend }
//   resend: { value, count, isComparative }, or null when there is no resend
// A/B: { key, name, tooltip, noDataText, isAB, value, count, a, b, resend }
//   a, b: { value, count }
//   resend: { a, b, average, count, isComparative }, or null
export const getTileValues = (message, channel) => {
  const { stats } = message;
  const isAB = !!stats.versionB;
  // The text channel has no resend line, as on the current Text tab.
  const hasResend = !!stats.smartResend && channel !== 'text';
  const noDataText = getNoDataText(message, isAB);

  return CHANNEL_TILES[channel].map(key => {
    const tile = {
      key,
      name: isAB ? `${TILE_NAMES[key]} Average` : TILE_NAMES[key],
      tooltip: TILE_TOOLTIPS[channel][key],
      noDataText,
    };
    const isComparative = COMPARATIVE_TILES.includes(key);
    const versionA = getVersionTile(
      key,
      channel,
      stats.email,
      stats.text,
      hasResend ? stats.smartResend.email : null
    );

    if (!isAB) {
      return {
        ...tile,
        value: versionA.value,
        count: versionA.count + versionA.resendCount,
        resend: hasResend
          ? {
              value: versionA.resendValue,
              count: versionA.resendCount,
              isComparative,
            }
          : null,
      };
    }

    const versionB = getVersionTile(
      key,
      channel,
      stats.versionB.email,
      stats.versionB.text,
      hasResend ? stats.smartResend.versionB?.email : null
    );
    return {
      ...tile,
      isAB: true,
      value: generateAverage(versionA.value, versionB.value),
      count: getAverage(versionA.count, versionB.count),
      a: { value: versionA.value, count: versionA.count },
      b: { value: versionB.value, count: versionB.count },
      resend: hasResend
        ? {
            a: versionA.resendValue,
            b: versionB.resendValue,
            average:
              versionA.resendValue || versionB.resendValue
                ? generateAverage(
                    versionA.resendValue || 0,
                    versionB.resendValue || 0
                  )
                : 0,
            count: getAverage(versionA.resendCount, versionB.resendCount),
            isComparative,
          }
        : null,
    };
  });
};

// ---------------------------------------------------------------------------
// Combined stats page tiles: each tile averages the selected messages' own
// tile values (getTileValues), over the messages counted for that tile.

// Tiles that only have email events. Under All channels they say "Email only".
export const EMAIL_ONLY_TILES = ['open', 'clickThrough', 'drop', 'spam'];

// Emails and texts sent for the message, A and B together.
export const getSent = ({ stats }) => ({
  email: stats.email.sent + (stats.versionB?.email.sent || 0),
  text: stats.text.sent + (stats.versionB?.text.sent || 0),
});

// Whether the message has stats for the tile under the channel: it sent
// something on the tile's channels. Scheduled messages have sent nothing, so
// they are never counted.
export const isCounted = (message, tileKey, channel) => {
  const sent = getSent(message);
  if (channel === 'text') return sent.text > 0;
  if (channel === 'all' && !EMAIL_ONLY_TILES.includes(tileKey)) {
    return sent.email + sent.text > 0;
  }
  return sent.email > 0;
};

const mean = values =>
  values.reduce((sum, value) => sum + value, 0) / values.length;

// The resend value of one message's tile: the A/B mean for an A/B message.
const getResendValue = tile =>
  tile.isAB ? tile.resend.average : tile.resend.value;

// The combined page tiles for the selected messages and the channel, in the
// shape StatCard takes: { key, name, tooltip, noDataText, value, count,
// resend, footnote }.
// - value: the mean of the counted messages' values ("30.0%")
// - count: the mean of their counts, to one decimal ("250.0")
// - resend: averaged over the counted messages that have a resend, or null
//   when none has
// - footnote: "Average of m of n messages" when some messages are not
//   counted, otherwise null
// - noDataText: "No data" when no message is counted
export const getCombinedTileValues = (messages, channel) =>
  CHANNEL_TILES[channel].map(key => {
    const tile = {
      key,
      name: TILE_NAMES[key],
      tooltip: TILE_TOOLTIPS[channel][key],
    };
    const counted = messages.filter(message =>
      isCounted(message, key, channel)
    );
    if (counted.length === 0) {
      return {
        ...tile,
        noDataText: 'No data',
        value: null,
        count: 0,
        resend: null,
        footnote: null,
      };
    }

    const tiles = counted.map(message =>
      getTileValues(message, channel).find(value => value.key === key)
    );
    const resent = tiles.filter(value => value.resend);
    return {
      ...tile,
      noDataText: null,
      value: `${mean(tiles.map(value => parseFloat(value.value))).toFixed(1)}%`,
      count: mean(tiles.map(value => Number(value.count))).toFixed(1),
      resend:
        resent.length > 0
          ? {
              value: `${mean(
                resent.map(value => parseFloat(getResendValue(value)) || 0)
              ).toFixed(1)}%`,
              count: mean(
                resent.map(value => Number(value.resend.count))
              ).toFixed(1),
              isComparative: COMPARATIVE_TILES.includes(key),
            }
          : null,
      footnote:
        counted.length < messages.length
          ? `Average of ${counted.length} of ${messages.length} messages`
          : null,
    };
  });
